'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementRef,
  forwardRef,
  useRef,
} from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint';
import { cn, generateRdsId } from '../../lib/utils';
import { type createStyleType } from '../../theme/types';
import { usePortalContainer } from '../PortalContainer/PortalContainerContext';
import { SheetDragHandle } from './SheetDragHandle';
import { SheetOverlay } from './SheetOverlay';
import { SheetPortal } from './SheetPortal';
import { useSheetDrag } from './useSheetDrag';

const sheetContentThemeableVars = [
  'r:[--bg:theme(sheetContent.bg.default)]',
  'r:[--overlay]',
] as const;

type SheetContentStyle = ReturnType<typeof createStyleType<typeof sheetContentThemeableVars>>;

const SHEET_CONTENT_RDS_ID = generateRdsId('sheet-content');

const sheetContentVariants = cva(
  `
  ${sheetContentThemeableVars.join(' ')}
  r:bg-(--bg)
  r:fixed r:z-overlay-background
  r:shadow-lg r:transition r:ease-in-out
  r:overflow-y-auto r:min-h-0
  r:data-[state=open]:animate-in
  r:data-[state=closed]:animate-out
  r:data-[state=closed]:duration-300
  r:data-[state=open]:duration-600
  `,
  {
    variants: {
      side: {
        bottom: `
          r:inset-x-0
          r:bottom-0
          r:max-h-[90dvh]
          r:border-t
          r:rounded-t-2xl
          r:sm:rounded-t-none
          r:data-[state=closed]:slide-out-to-bottom
          r:data-[state=open]:slide-in-from-bottom`,
        left: `
          r:inset-y-0
          r:left-0
          r:h-full
          r:w-3/4
          r:border-r
          r:data-[state=closed]:slide-out-to-left
          r:data-[state=open]:slide-in-from-left r:sm:max-w-[25rem]`,
        right: `
          r:inset-y-0
          r:right-0
          r:h-full
          r:w-3/4
          r:border-l
          r:data-[state=closed]:slide-out-to-right
          r:data-[state=open]:slide-in-from-right r:sm:max-w-[25rem]`,
      },
      layout: {
        inset: 'r:gap-4 r:pb-6 r:pl-6 r:pr-6',
        bleed: `
          r:flex r:min-h-0 r:flex-col r:overflow-hidden
          r:[&_[data-rds-id=rds-sheet-footer]]:p-6
          r:[&_[data-rds-id=rds-sheet-footer]]:border-t
          r:[&_[data-rds-id=rds-sheet-footer]]:border-[theme(separator.bg.default)]
        `,
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

type SheetSide = NonNullable<VariantProps<typeof sheetContentVariants>['side']>;
type ResponsiveSheetSide = SheetSide | 'responsive-right' | 'responsive-left';

interface SheetContentProps extends ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  side?: ResponsiveSheetSide;
  layout?: 'inset' | 'bleed';
  shouldHideBackdrop?: boolean;
  container?: ComponentProps<typeof SheetPortal>['container'];
  style?: SheetContentStyle & CSSProperties;
  overlayClassName?: string;
}

const resolveResponsiveSide = (side: ResponsiveSheetSide, isLargeScreen: boolean): SheetSide => {
  if (side === 'responsive-right') return isLargeScreen ? 'right' : 'bottom';
  if (side === 'responsive-left') return isLargeScreen ? 'left' : 'bottom';
  return side;
};

const SheetContent = forwardRef<ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  (
    {
      side = 'responsive-right',
      layout = 'inset',
      shouldHideBackdrop = false,
      className,
      style,
      children,
      container,
      overlayClassName,
      ...props
    },
    ref
  ) => {
    const isLargeScreen = useBreakpoint('sm');
    const effectiveSide = resolveResponsiveSide(side, isLargeScreen);
    const isBottomSheet = effectiveSide === 'bottom';
    const isMobileSize = !isLargeScreen;
    const contentRef = useRef<HTMLDivElement>(null);
    const containerFromContext = usePortalContainer();

    const { sheetMotionStyle, dragBinds, shouldRenderDragHandle } = useSheetDrag({
      isBottomSheet,
      isMobileSize,
      contentRef,
      style,
    });

    return (
      <SheetPortal container={container ?? containerFromContext}>
        <SheetOverlay
          hasBackdrop={!shouldHideBackdrop}
          style={{ '--overlay': style?.['--overlay'] } as CSSProperties & { '--overlay'?: string }}
          className={overlayClassName}
        />
        <SheetPrimitive.Content
          ref={(el) => {
            contentRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          className={cn(
            SHEET_CONTENT_RDS_ID,
            sheetContentVariants({ side: effectiveSide, layout }),
            !shouldRenderDragHandle && 'r:pt-5',
            className
          )}
          style={sheetMotionStyle}
          aria-describedby={undefined}
          data-rds-id={SHEET_CONTENT_RDS_ID}
          data-sds-id="sheet-content"
          {...dragBinds()}
          {...props}
        >
          {shouldRenderDragHandle && <SheetDragHandle />}
          {children}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

export { type ResponsiveSheetSide, SheetContent, type SheetContentProps, type SheetSide };
